import { Check } from "lucide-react";
import type { CheckoutStep, CheckoutStepId } from "./useCheckoutFlow";

// Phase 4x checkout-polish — horizontal progress strip above the
// accordion. Closes the grade-doc's #1 design-checklist gap:
// "obvious stepper at the top." The accordion-header step numbers
// already exist, but a customer scanning a long form reads "where
// am I" much faster from a horizontal indicator than from a vertical
// stack of headers.
//
// Layout:
//   ●━━━●━━━①━━━○━━━○━━━○         desktop (labels under each dot)
//   ●━━━●━━━①━━━○━━━○━━━○         mobile (labels hidden; caption
//   Step 3 of 7                    above carries the human read)
//
// State semantics:
//   - "complete" — step.complete is true (brand-red filled with ✓)
//   - "current"  — first incomplete + unlocked step (brand-red ring,
//                  numbered text inside)
//   - "upcoming" — locked, awaiting earlier steps (zinc dot)

const STEP_LABEL: Record<CheckoutStepId, string> = {
  cart: "Cart",
  address: "Address",
  date: "Dates",
  account: "Account",
  consent: "Drop-off",
  coi: "COI",
  payment: "Payment"
};

type StepState = "complete" | "current" | "upcoming";

function deriveState(step: CheckoutStep, currentId: CheckoutStepId | null): StepState {
  if (step.complete) return "complete";
  if (step.id === currentId) return "current";
  return "upcoming";
}

function findCurrentId(
  steps: Record<CheckoutStepId, CheckoutStep>,
  orderedSteps: CheckoutStepId[]
): CheckoutStepId | null {
  return orderedSteps.find((id) => !steps[id].complete) ?? null;
}

export interface CheckoutProgressStripProps {
  steps: Record<CheckoutStepId, CheckoutStep>;
  orderedSteps: CheckoutStepId[];
}

export function CheckoutProgressStrip({
  steps,
  orderedSteps
}: CheckoutProgressStripProps): JSX.Element {
  const currentId = findCurrentId(steps, orderedSteps);
  const completeCount = orderedSteps.filter((id) => steps[id].complete).length;
  const total = orderedSteps.length;
  const allComplete = completeCount === total;
  // When every step is done, the "current" step is conceptually
  // "Confirm" (the next + final action). We surface that as a copy
  // change on the caption rather than rendering a fake N+1 pill.
  const positionLabel = allComplete
    ? "Ready to confirm"
    : `Step ${completeCount + 1} of ${total}`;

  return (
    <div className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm">
      <p
        className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-red"
        data-testid="checkout-progress-caption"
      >
        {positionLabel}
      </p>
      <ol
        role="list"
        aria-label="Checkout progress"
        className="mt-3 flex items-center gap-1 sm:gap-2"
      >
        {orderedSteps.map((id, idx) => {
          const step = steps[id];
          const state = deriveState(step, currentId);
          return (
            <li
              key={id}
              data-testid={`checkout-progress-step-${id}`}
              data-state={state}
              aria-current={state === "current" ? "step" : undefined}
              className="flex flex-1 items-center gap-1 first:flex-initial last:flex-initial sm:gap-2"
            >
              <ProgressDot state={state} index={idx} />
              <span
                className={
                  "hidden whitespace-nowrap text-xs font-medium sm:inline " +
                  (state === "current"
                    ? "text-brand-black"
                    : state === "complete"
                      ? "text-brand-red"
                      : "text-zinc-400")
                }
              >
                {STEP_LABEL[id]}
              </span>
              {idx < orderedSteps.length - 1 ? <ProgressConnector state={state} /> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ProgressDot({ state, index }: { state: StepState; index: number }): JSX.Element {
  const base =
    "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold transition";
  if (state === "complete") {
    return (
      <span aria-hidden="true" className={`${base} bg-brand-red text-brand-white`}>
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (state === "current") {
    return (
      <span
        aria-hidden="true"
        className={`${base} bg-brand-white text-brand-red ring-2 ring-brand-red`}
      >
        {index + 1}
      </span>
    );
  }
  return (
    <span aria-hidden="true" className={`${base} bg-zinc-100 text-zinc-400 ring-1 ring-zinc-200`}>
      {index + 1}
    </span>
  );
}

function ProgressConnector({ state }: { state: StepState }): JSX.Element {
  const tone =
    state === "complete" ? "bg-brand-red" : state === "current" ? "bg-brand-red/40" : "bg-zinc-200";
  return <span aria-hidden="true" className={`h-px flex-1 ${tone}`} />;
}
